// Monthly MDCF page — PM POSHAN School Monthly Data Capture Format.
//
// Two panels:
//  1) Monthly MDCF Data — the manual monthly inputs (Fund Details, Cooks,
//     Food Grains opening/received, Health Status, Inspection, Incidents).
//  2) Live Preview — the completed MDCF form ready to Print / Save-as-PDF.
//
// All *automatic* fields (School Days, MDM Days, meals served, cooking cost
// expenditure, rice consumption) come from the server aggregation which uses
// the existing Daily Entry data via the existing calculation engine — this
// module never re-implements calculation logic.
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Printer, Table2, Save, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, RefreshCw, Copy, School, XCircle, Cloud, CloudOff, Eye, Download, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/mdmData";
import { formatRiceKg } from "../lib/reportFormat";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = Array.from({ length: 11 }, (_, i) => 2020 + i);
const CATS = [
  { key: "balvatika", label: "Bal Vatika" },
  { key: "primary", label: "Primary" },
  { key: "upper_primary", label: "Upper Primary" },
];

// Chip key → Section testid mapping for anchor-scroll jumps.
const CHIP_TO_SECTION = {
  profile:   "sec-school-profile",
  funds:     "sec-fund",
  cooks:     "sec-cook",
  food:      "sec-food",
  health:    "sec-health",
  insp:      "sec-inspection",
  incidents: "sec-incidents",
};

// Money helper — accepts strings from Input and returns numbers.
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmtMoney = (v) => formatCurrency(num(v));

// Empty template for the manual monthly doc.
const emptyMonthly = () => ({
  school_details: {},
  fund_details: {
    cookingCost: {
      balvatika: { opening: "", received: "" },
      primary: { opening: "", received: "" },
      upper_primary: { opening: "", received: "" },
    },
    cookCumHelper: { opening: "", received: "", expenditure: "" },
    mme: { opening: "", received: "", expenditure: "" },
    bankBalanceMatches: null,
  },
  cook_cum_helper: [{ name: "", gender: "", category: "", paymentMode: "", amountReceived: "" }],
  food_grains: {
    balvatika: { wheat: { opening: "", received: "" }, rice: { opening: "", received: "" } },
    primary: { wheat: { opening: "", received: "" }, rice: { opening: "", received: "" } },
    upper_primary: { wheat: { opening: "", received: "" }, rice: { opening: "", received: "" } },
  },
  health_status: { ifaBoys: "", ifaGirls: "", rbskScreened: "", rbskReferred: "" },
  inspection: { schoolInspectionDone: "", taskForce: "", districtOfficials: "", blockOfficials: "", smcMembers: "" },
  untoward_incidents: 0,
});

// Empty School Profile template (persistent, one per user).
const emptySchoolProfile = () => ({
  schoolType: "", schoolCategory: "", kitchenType: "", ngoShg: "",
  state: "", district: "", block: "", villageWard: "",
  enrolment: { balvatika: "", primary: "", upper_primary: "", high_school: "", total: 0 },
});

// Copy every relevant School Profile field into the monthly school_details cell.
const seedSchoolDetailsFromProfile = (p) => {
  if (!p) return {};
  const enrol = p.enrolment || {};
  const total = ["balvatika", "primary", "upper_primary", "high_school"]
    .reduce((s, k) => s + (Number(enrol[k]) || 0), 0);
  return {
    schoolType: p.schoolType || "",
    schoolCategory: p.schoolCategory || "",
    kitchenType: p.kitchenType || "",
    ngoShg: p.ngoShg || "",
    state: p.state || "",
    district: p.district || "",
    block: p.block || "",
    villageWard: p.villageWard || "",
    enrolment: {
      balvatika: enrol.balvatika ?? "",
      primary: enrol.primary ?? "",
      upper_primary: enrol.upper_primary ?? "",
      high_school: enrol.high_school ?? "",
    },
    enrolmentTotal: total,
  };
};

// Completeness: which manual sections still need input.
const nonEmpty = (v) => v !== null && v !== undefined && String(v).trim() !== "";
const computeCompleteness = (m, sp) => {
  // 1. School Profile — needs Type, Category, Kitchen Type + at least one enrolment
  const enrol = (sp || {}).enrolment || {};
  const hasEnrol = ["balvatika","primary","upper_primary","high_school"].some((k) => Number(enrol[k]) > 0);
  const profile = nonEmpty(sp?.schoolType) && nonEmpty(sp?.schoolCategory) && nonEmpty(sp?.kitchenType) && hasEnrol;

  // 2. Fund Details — every cookingCost cat + cookCumHelper + mme has opening filled (0 counts)
  const cc = m.fund_details?.cookingCost || {};
  const funds =
    ["balvatika","primary","upper_primary"].every((c) => nonEmpty(cc?.[c]?.opening) && nonEmpty(cc?.[c]?.received)) &&
    nonEmpty(m.fund_details?.cookCumHelper?.opening) && nonEmpty(m.fund_details?.cookCumHelper?.received) && nonEmpty(m.fund_details?.cookCumHelper?.expenditure) &&
    nonEmpty(m.fund_details?.mme?.opening) && nonEmpty(m.fund_details?.mme?.received) && nonEmpty(m.fund_details?.mme?.expenditure) &&
    (m.fund_details?.bankBalanceMatches === true || m.fund_details?.bankBalanceMatches === false);

  // 3. Cook Cum Helper — at least one row with name + amount
  const cooks = (m.cook_cum_helper || []).some((r) => nonEmpty(r?.name) && nonEmpty(r?.amountReceived));

  // 4. Food Grains — every category has both wheat & rice opening + received
  const fg = m.food_grains || {};
  const food = ["balvatika","primary","upper_primary"].every((c) =>
    nonEmpty(fg?.[c]?.wheat?.opening) && nonEmpty(fg?.[c]?.wheat?.received) &&
    nonEmpty(fg?.[c]?.rice?.opening) && nonEmpty(fg?.[c]?.rice?.received)
  );

  // 5. Health — all 4 fields
  const health = ["ifaBoys","ifaGirls","rbskScreened","rbskReferred"].every((k) => nonEmpty(m.health_status?.[k]));

  // 6. Inspection — Yes/No plus any note field
  const insp = nonEmpty(m.inspection?.schoolInspectionDone);

  // 7. Untoward Incidents — always present (numeric, defaults to 0) → treat as complete when saved doc exists
  const incidents = m.untoward_incidents !== null && m.untoward_incidents !== undefined && m.untoward_incidents !== "";

  const sections = { profile, funds, cooks, food, health, insp, incidents };
  const total = Object.keys(sections).length;
  const done = Object.values(sections).filter(Boolean).length;
  return { sections, done, total, pct: Math.round((done / total) * 100) };
};

export default function MonthlyMDCF() {
  const { user, isAuthed } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-based
  const [monthly, setMonthly] = useState(emptyMonthly());
  const [report, setReport] = useState(null);
  const [schoolProfile, setSchoolProfile] = useState(emptySchoolProfile());
  const [busy, setBusy] = useState(false);
  const [busyProfile, setBusyProfile] = useState(false);
  const [busyCopy, setBusyCopy] = useState(false);
  const [loading, setLoading] = useState(false);
  // Generated PDF cache (blob + object URL + filename). One PDF, three actions.
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [busyPdf, setBusyPdf] = useState(false);
  // Auto-save + controlled section state
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle|dirty|saving|saved|error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [openMap, setOpenMap] = useState({}); // sectionKey -> bool
  const autoSaveTimerRef = useRef(null);
  const suppressAutoSaveRef = useRef(true); // block right after load()

  const load = async () => {
    if (!isAuthed) return;
    setLoading(true);
    suppressAutoSaveRef.current = true;
    try {
      const [r, sp] = await Promise.all([
        api.mdcfReport(year, month),
        api.mdcfSchoolProfileGet().catch(() => ({})),
      ]);
      setReport(r);
      setSchoolProfile({ ...emptySchoolProfile(), ...(sp || {}), enrolment: { ...emptySchoolProfile().enrolment, ...((sp || {}).enrolment || {}) } });
      const md = r?.monthly_data;
      if (md) {
        // Preserve full shape by merging with the empty template
        setMonthly({ ...emptyMonthly(), ...md, fund_details: { ...emptyMonthly().fund_details, ...(md.fund_details || {}) },
          food_grains: { ...emptyMonthly().food_grains, ...(md.food_grains || {}) },
          health_status: { ...emptyMonthly().health_status, ...(md.health_status || {}) },
          inspection: { ...emptyMonthly().inspection, ...(md.inspection || {}) },
          cook_cum_helper: (md.cook_cum_helper && md.cook_cum_helper.length) ? md.cook_cum_helper : emptyMonthly().cook_cum_helper,
        });
        setLastSavedAt(md.updated_at ? new Date(md.updated_at) : null);
        setAutoSaveState("saved");
      } else {
        // No saved monthly doc → seed school_details from persistent School Profile
        const t = emptyMonthly();
        t.school_details = seedSchoolDetailsFromProfile(sp || {});
        setMonthly(t);
        setLastSavedAt(null);
        setAutoSaveState("idle");
      }
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Premium is required to use Monthly MDCF");
      else toast.error("Could not load MDCF data");
      setReport(null);
    } finally {
      setLoading(false);
      // Release auto-save gate on next tick so the state-setters above don't fire it
      setTimeout(() => { suppressAutoSaveRef.current = false; }, 100);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year, month, isAuthed]);

  // Auto-save monthly manual data (debounced 2s after last edit).
  useEffect(() => {
    if (suppressAutoSaveRef.current) return;
    if (!isAuthed || loading || busy) return;
    setAutoSaveState("dirty");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      autoSaveTimerRef.current = null;
      try {
        setAutoSaveState("saving");
        const saved = await api.mdcfSave({ year, month, ...monthly, untoward_incidents: Number(monthly.untoward_incidents) || 0 });
        setLastSavedAt(saved?.updated_at ? new Date(saved.updated_at) : new Date());
        setAutoSaveState("saved");
      } catch (e) {
        setAutoSaveState("error");
      }
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthly]);

  // Flush pending auto-save when the tab is being hidden — protects unsaved
  // keystrokes without firing on every keystroke re-render.
  const flushRef = useRef({ year, month, monthly });
  flushRef.current = { year, month, monthly };
  useEffect(() => {
    const flush = () => {
      if (!autoSaveTimerRef.current) return;
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
      const { year: y, month: m, monthly: mn } = flushRef.current;
      // fire-and-forget; browser may not wait for the response but the payload leaves the tab
      api.mdcfSave({ year: y, month: m, ...mn, untoward_incidents: Number(mn.untoward_incidents) || 0 }).catch(() => {});
    };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", flush);
    };
  }, []);

  const save = async () => {
    setBusy(true);
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    try {
      await api.mdcfSave({ year, month, ...monthly, untoward_incidents: Number(monthly.untoward_incidents) || 0 });
      setLastSavedAt(new Date());
      setAutoSaveState("saved");
      toast.success("Monthly MDCF data saved");
      await load();
    } catch (e) {
      setAutoSaveState("error");
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const saveProfile = async () => {
    setBusyProfile(true);
    try {
      const p = await api.mdcfSchoolProfileSave(schoolProfile);
      setSchoolProfile({ ...emptySchoolProfile(), ...p, enrolment: { ...emptySchoolProfile().enrolment, ...(p.enrolment || {}) } });
      // If the current monthly doc has empty school_details, seed it from profile
      setMonthly((m) => {
        const hasAny = Object.values(m.school_details || {}).some((v) => v !== "" && v !== null && v !== undefined && !(typeof v === "object" && Object.keys(v).length === 0));
        return hasAny ? m : { ...m, school_details: seedSchoolDetailsFromProfile(p) };
      });
      toast.success("School profile saved — will auto-fill every month");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusyProfile(false); }
  };

  const copyFromPrev = async () => {
    if (!window.confirm("Copy last month's manual data into this month? Received amounts, cook payments, health, inspection & incident fields will be cleared. Opening balances roll forward from previous closing.")) return;
    setBusyCopy(true);
    try {
      await api.mdcfCopyFromPrev(year, month);
      toast.success("Copied from previous month — please review & save");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    } finally { setBusyCopy(false); }
  };

  // Invalidate the cached PDF whenever the report data or period changes.
  useEffect(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfBlob(null); setPdfUrl(null); setPdfName("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);
  // Cleanup on unmount
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const generatePdf = async () => {
    // Flush any pending auto-save first so the PDF reflects the freshest data.
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
      try {
        await api.mdcfSave({ year, month, ...monthly, untoward_incidents: Number(monthly.untoward_incidents) || 0 });
        setAutoSaveState("saved");
        setLastSavedAt(new Date());
      } catch (e) { /* auto-save error surfaced elsewhere */ }
    }
    setBusyPdf(true);
    try {
      const { blob, filename } = await api.mdcfPdf(year, month);
      if (!blob || blob.size === 0 || blob.type !== "application/pdf") {
        throw new Error("Unable to generate the MDCF PDF correctly. Please try again.");
      }
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfBlob(blob); setPdfUrl(url); setPdfName(filename);
      toast.success("MDCF PDF ready — 2 pages generated");
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "PDF generation failed");
    } finally { setBusyPdf(false); }
  };

  const viewPdf = () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };
  const downloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl; a.download = pdfName || "MDCF.pdf";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const shareOnWhatsApp = async () => {
    if (!pdfBlob) return;
    const monthLabel = `${MONTHS[month - 1]} ${year}`;
    const msg = `PM POSHAN Monthly MDCF Report — ${monthLabel}\n\nPlease find the generated MDCF PDF.`;
    // Best case: Web Share API with the actual PDF file (mobile Safari/Chrome).
    try {
      const file = new File([pdfBlob], pdfName || "MDCF.pdf", { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await navigator.share({ files: [file], title: "PM POSHAN Monthly MDCF", text: msg });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user cancelled
      // fall through to wa.me
    }
    // Fallback: open a wa.me chat with a prefilled message. The user attaches the
    // already-downloaded PDF manually (browsers cannot silently attach files).
    toast.message("Attach the downloaded PDF in WhatsApp after the chat opens.");
    if (!pdfBlob) return;
    // Trigger a download so the file is available on-device to attach
    downloadPdf();
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const auto = report?.automatic || { school_days: 0, mdm_days: 0, holidays: 0, dry_ration_days: 0,
    total_meals: { balvatika:0, primary:0, upper_primary:0, high_school:0 },
    cooking_cost: { balvatika:0, primary:0, upper_primary:0, high_school:0 },
    rice_kg: { balvatika:0, primary:0, upper_primary:0, high_school:0 } };

  // Completeness per section
  const completeness = useMemo(() => computeCompleteness(monthly, schoolProfile), [monthly, schoolProfile]);

  const setSectionOpen = (key, val) => setOpenMap((m) => ({ ...m, [key]: val }));
  const jumpToSection = (chipKey) => {
    const sectionKey = CHIP_TO_SECTION[chipKey];
    if (!sectionKey) return;
    setSectionOpen(sectionKey, true);
    // Wait for section to expand before scrolling
    setTimeout(() => {
      const el = document.getElementById(`mdcf-anchor-${sectionKey}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="mdcf-page">
      {/* Header + selectors */}
      <div className="print:hidden">
        <div className="chip chip-green"><FileText size={14}/> Monthly MDCF</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">School Monthly Data Capture Format</h1>
        <p className="mt-1 text-slate-600">Auto-populated from Daily Entry — add fund, food-grain, health and inspection details below.</p>
      </div>

      <div className="print:hidden rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Year</Label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1 h-11 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold" data-testid="mdcf-year">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Month</Label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="mt-1 h-11 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold" data-testid="mdcf-month">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="mdcf-reload"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Reload</Button>
        <Button variant="outline" onClick={copyFromPrev} disabled={busyCopy} data-testid="mdcf-copy-prev"><Copy size={14}/> {busyCopy ? "Copying…" : "Copy Last Month"}</Button>
        <div className="flex-1"/>
        <AutoSaveIndicator state={autoSaveState} savedAt={lastSavedAt}/>
        <Button onClick={generatePdf} disabled={busyPdf} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="mdcf-generate-pdf">
          {busyPdf ? <Loader2 size={14} className="animate-spin"/> : <FileText size={14}/>} {busyPdf ? "Generating…" : "Generate MDCF PDF"}
        </Button>
        <Button onClick={() => exportCsv(report, monthly)} variant="outline" data-testid="mdcf-csv"><Table2 size={14}/> CSV</Button>
        <Button onClick={save} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mdcf-save"><Save size={14}/> {busy ? "Saving…" : "Save Monthly Data"}</Button>
      </div>

      {/* PDF action bar — shown only after successful generation */}
      {pdfUrl && (
        <PdfActionBar filename={pdfName} onView={viewPdf} onDownload={downloadPdf} onShare={shareOnWhatsApp}/>
      )}

      {/* Completeness overview */}
      <CompletenessBar completeness={completeness} onJump={jumpToSection}/>

      {/* Monthly Data Entry (manual) */}
      <div className="print:hidden space-y-3">
        <Section title="School Profile (reusable)" testid="sec-school-profile"
                 defaultOpen={!completeness.sections.profile} status={completeness.sections.profile}
                 open={openMap["sec-school-profile"]} onToggle={(v) => setSectionOpen("sec-school-profile", v)}>
          <SchoolProfileForm profile={schoolProfile} setProfile={setSchoolProfile} onSave={saveProfile} busy={busyProfile}/>
        </Section>
        <Section title="Fund Details" testid="sec-fund"
                 defaultOpen={!completeness.sections.funds} status={completeness.sections.funds}
                 open={openMap["sec-fund"]} onToggle={(v) => setSectionOpen("sec-fund", v)}>
          <FundDetailsForm monthly={monthly} setMonthly={setMonthly} auto={auto}/>
        </Section>
        <Section title="Cook Cum Helper Payment" testid="sec-cook" status={completeness.sections.cooks}
                 open={openMap["sec-cook"]} onToggle={(v) => setSectionOpen("sec-cook", v)}>
          <CookHelperForm monthly={monthly} setMonthly={setMonthly}/>
        </Section>
        <Section title="Food Grains — Opening & Received" testid="sec-food" status={completeness.sections.food}
                 open={openMap["sec-food"]} onToggle={(v) => setSectionOpen("sec-food", v)}>
          <FoodGrainsForm monthly={monthly} setMonthly={setMonthly} auto={auto}/>
        </Section>
        <Section title="Children Health Status" testid="sec-health" status={completeness.sections.health}
                 open={openMap["sec-health"]} onToggle={(v) => setSectionOpen("sec-health", v)}>
          <HealthForm monthly={monthly} setMonthly={setMonthly}/>
        </Section>
        <Section title="School Inspection" testid="sec-inspection" status={completeness.sections.insp}
                 open={openMap["sec-inspection"]} onToggle={(v) => setSectionOpen("sec-inspection", v)}>
          <InspectionForm monthly={monthly} setMonthly={setMonthly}/>
        </Section>
        <Section title="Untoward Incidents" testid="sec-incidents" status={completeness.sections.incidents}
                 open={openMap["sec-incidents"]} onToggle={(v) => setSectionOpen("sec-incidents", v)}>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Number of Untoward Incidents</Label>
            <Input type="number" min="0" value={monthly.untoward_incidents ?? 0}
              onChange={(e) => setMonthly((m) => ({ ...m, untoward_incidents: Math.max(0, Number(e.target.value) || 0) }))}
              className="h-11 mt-1 max-w-xs" data-testid="mdcf-incidents"/>
          </div>
        </Section>
      </div>

      {/* MDCF Preview / Printable Form */}
      <MDCFPrintable report={report} monthly={monthly} school={{ name: user?.school_name || "", udise: user?.udise || "", full_name: user?.full_name || "" }} year={year} month={month}/>
    </div>
  );
}

// ─── Collapsible section wrapper ──────────────────────────────────────────
function Section({ title, testid, defaultOpen = false, status, open: openProp, onToggle, children }) {
  const [openState, setOpenState] = useState(defaultOpen);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : openState;
  const toggle = () => {
    const next = !open;
    if (onToggle) onToggle(next);
    if (!isControlled) setOpenState(next);
  };
  const hasStatus = typeof status === "boolean";
  return (
    <div id={`mdcf-anchor-${testid}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden scroll-mt-24" data-testid={testid}>
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50">
        <span className="font-display font-bold text-slate-900 flex items-center gap-2">
          {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>} {title}
        </span>
        {hasStatus && (
          status
            ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold" data-testid={`${testid}-status-ok`}><CheckCircle2 size={14}/> Complete</span>
            : <span className="inline-flex items-center gap-1 text-red-600 text-xs font-bold" data-testid={`${testid}-status-pending`}><XCircle size={14}/> Pending</span>
        )}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// ─── Auto-save status pill ────────────────────────────────────────────────
function AutoSaveIndicator({ state, savedAt }) {
  const time = savedAt ? new Date(savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  let cls = "bg-slate-100 text-slate-600 border-slate-200";
  let Icon = Cloud;
  let text = "Not saved yet";
  if (state === "saving") { cls = "bg-amber-50 text-amber-800 border-amber-200"; Icon = Cloud; text = "Saving…"; }
  else if (state === "saved") { cls = "bg-emerald-50 text-emerald-800 border-emerald-200"; Icon = CheckCircle2; text = savedAt ? `Auto-saved · ${time}` : "Auto-saved"; }
  else if (state === "dirty") { cls = "bg-slate-50 text-slate-700 border-slate-200"; Icon = Cloud; text = "Unsaved changes…"; }
  else if (state === "error") { cls = "bg-red-50 text-red-700 border-red-200"; Icon = CloudOff; text = "Auto-save failed"; }
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-xs font-semibold ${cls}`} data-testid="mdcf-autosave-status" data-state={state}>
      <Icon size={14}/> {text}
    </span>
  );
}

// ─── PDF action bar — View / Download / WhatsApp share (mobile-friendly) ─
function PdfActionBar({ filename, onView, onDownload, onShare }) {
  return (
    <div className="print:hidden rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-4"
         data-testid="mdcf-pdf-action-bar">
      <div className="flex items-center gap-2 mb-3 text-emerald-800 font-display font-bold text-sm">
        <CheckCircle2 size={16}/> MDCF Generated Successfully
        <span className="ml-auto text-xs font-mono text-emerald-700/80 truncate max-w-[60%]" title={filename}>
          {filename}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onView} className="min-h-[44px] bg-slate-900 hover:bg-slate-800 text-white flex-1 sm:flex-none" data-testid="mdcf-pdf-view">
          <Eye size={16}/> View PDF
        </Button>
        <Button onClick={onDownload} className="min-h-[44px] bg-blue-700 hover:bg-blue-800 text-white flex-1 sm:flex-none" data-testid="mdcf-pdf-download">
          <Download size={16}/> Download PDF
        </Button>
        <Button onClick={onShare} className="min-h-[44px] bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none" data-testid="mdcf-pdf-share">
          <Send size={16}/> Share on WhatsApp
        </Button>
      </div>
    </div>
  );
}


function CompletenessBar({ completeness, onJump }) {
  const { done, total, pct, sections } = completeness;
  const chips = [
    ["profile", "School Profile"],
    ["funds", "Fund Details"],
    ["cooks", "Cook Payment"],
    ["food", "Food Grains"],
    ["health", "Health"],
    ["insp", "Inspection"],
    ["incidents", "Incidents"],
  ];
  const barColor = pct === 100 ? "bg-emerald-600" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="print:hidden rounded-2xl border border-slate-200 bg-white p-4" data-testid="mdcf-completeness">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-bold text-slate-900 text-sm">MDCF Completeness</div>
        <div className="text-xs font-bold text-slate-700" data-testid="mdcf-completeness-pct">{done}/{total} sections · {pct}%</div>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }}/>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {chips.map(([k, lbl]) => (
          <button
            key={k}
            type="button"
            onClick={() => onJump && onJump(k)}
            title={sections[k] ? `${lbl} is complete — jump to section` : `${lbl} still pending — jump to section`}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition hover:shadow-sm hover:-translate-y-px active:translate-y-0
              ${sections[k]
                ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
                : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"}`}
            data-testid={`mdcf-chip-${k}`}>
            {sections[k] ? <CheckCircle2 size={12}/> : <XCircle size={12}/>} {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── School Profile Form (persistent, auto-fills every month) ────────────
function SchoolProfileForm({ profile, setProfile, onSave, busy }) {
  const set = (field, v) => setProfile((p) => ({ ...p, [field]: v }));
  const setEnrol = (field, v) => setProfile((p) => ({ ...p, enrolment: { ...(p.enrolment || {}), [field]: v } }));
  const enrol = profile.enrolment || {};
  const total = ["balvatika","primary","upper_primary","high_school"].reduce((s, k) => s + (Number(enrol[k]) || 0), 0);
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500 -mt-1">Fill this once per school. Values auto-fill into every month's MDCF cells.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldSelect label="School Type" value={profile.schoolType} onChange={(v) => set("schoolType", v)} testid="sp-type"
          options={["Primary","Upper Primary","Composite","Secondary","Higher Secondary","Other"]}/>
        <FieldSelect label="School Category" value={profile.schoolCategory} onChange={(v) => set("schoolCategory", v)} testid="sp-category"
          options={["Government","Government Aided","Local Body","Madrasa","Other"]}/>
        <FieldSelect label="Kitchen Type" value={profile.kitchenType} onChange={(v) => set("kitchenType", v)} testid="sp-kitchen"
          options={["On-Site Cooked","Centralised Kitchen","NGO Supplied","SHG Supplied","Other"]}/>
        <FieldText label="NGO / SHG Name (if any)" value={profile.ngoShg} onChange={(v) => set("ngoShg", v)} testid="sp-ngo"/>
        <FieldText label="State / UT" value={profile.state} onChange={(v) => set("state", v)} testid="sp-state"/>
        <FieldText label="District" value={profile.district} onChange={(v) => set("district", v)} testid="sp-district"/>
        <FieldText label="Block / NP" value={profile.block} onChange={(v) => set("block", v)} testid="sp-block"/>
        <FieldText label="Village / Ward" value={profile.villageWard} onChange={(v) => set("villageWard", v)} testid="sp-village"/>
      </div>
      <div>
        <div className="font-display font-bold text-sm text-slate-900 mb-2">Enrolment by Class</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <FieldNum label="Bal Vatika"    value={enrol.balvatika}     onChange={(v) => setEnrol("balvatika", v)}     testid="sp-enrol-balvatika"/>
          <FieldNum label="Primary (1–5)" value={enrol.primary}       onChange={(v) => setEnrol("primary", v)}       testid="sp-enrol-primary"/>
          <FieldNum label="Upper (6–8)"   value={enrol.upper_primary} onChange={(v) => setEnrol("upper_primary", v)} testid="sp-enrol-upper"/>
          <FieldNum label="High (9–10)"   value={enrol.high_school}   onChange={(v) => setEnrol("high_school", v)}   testid="sp-enrol-high"/>
        </div>
        <p className="mt-2 text-xs text-slate-600">Total Enrolment: <b data-testid="sp-enrol-total">{total}</b></p>
      </div>
      <div>
        <Button onClick={onSave} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="sp-save">
          <School size={14}/> {busy ? "Saving…" : "Save School Profile"}
        </Button>
      </div>
    </div>
  );
}

const FieldText = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-11 mt-1" data-testid={testid}/>
  </div>
);
const FieldNum = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
    <Input type="number" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-11 mt-1" data-testid={testid}/>
  </div>
);
const FieldSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</Label>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid={testid}>
      <option value="">— Select —</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const NumInput = ({ value, onChange, testid, placeholder = "0" }) => (
  <Input type="number" step="0.01" value={value ?? ""} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid}/>
);

// ─── Fund Details ─────────────────────────────────────────────────────────
function FundDetailsForm({ monthly, setMonthly, auto }) {
  const setCC = (cat, field, v) => setMonthly((m) => ({
    ...m, fund_details: { ...m.fund_details, cookingCost: { ...m.fund_details.cookingCost,
      [cat]: { ...m.fund_details.cookingCost[cat], [field]: v } } }
  }));
  const setBucket = (bucket, field, v) => setMonthly((m) => ({
    ...m, fund_details: { ...m.fund_details, [bucket]: { ...m.fund_details[bucket], [field]: v } }
  }));
  const setBank = (v) => setMonthly((m) => ({ ...m, fund_details: { ...m.fund_details, bankBalanceMatches: v } }));
  return (
    <div className="overflow-x-auto">
      <p className="text-xs text-slate-500 mb-2">Cooking cost <b>Expenditure</b> is auto-summed from Daily Entry. You only enter Opening & Received.</p>
      <table className="w-full text-left text-sm border">
        <thead className="bg-slate-50 border-b">
          <tr>
            <Th>Component</Th><Th>Opening (₹)</Th><Th>Received (₹)</Th><Th>Expenditure (₹)</Th><Th>Closing (₹)</Th>
          </tr>
        </thead>
        <tbody>
          {CATS.map((c) => {
            const cell = monthly.fund_details.cookingCost[c.key] || {};
            const expenditure = num(auto.cooking_cost[c.key]);
            const closing = num(cell.opening) + num(cell.received) - expenditure;
            return (
              <tr key={c.key} className="border-b" data-testid={`fd-cc-${c.key}`}>
                <Td>Cooking Cost — {c.label}</Td>
                <Td><NumInput value={cell.opening} onChange={(v) => setCC(c.key, "opening", v)} testid={`fd-cc-${c.key}-open`}/></Td>
                <Td><NumInput value={cell.received} onChange={(v) => setCC(c.key, "received", v)} testid={`fd-cc-${c.key}-recv`}/></Td>
                <Td className="text-slate-600" data-testid={`fd-cc-${c.key}-exp`}>{fmtMoney(expenditure)} <span className="text-[10px] text-emerald-700">AUTO</span></Td>
                <Td className="font-semibold text-slate-900">{fmtMoney(closing)}</Td>
              </tr>
            );
          })}
          {[
            { key: "cookCumHelper", label: "Cook Cum Helper" },
            { key: "mme", label: "School Expenses : MME Expenses" },
          ].map((row) => {
            const cell = monthly.fund_details[row.key] || {};
            const closing = num(cell.opening) + num(cell.received) - num(cell.expenditure);
            return (
              <tr key={row.key} className="border-b" data-testid={`fd-${row.key}`}>
                <Td>{row.label}</Td>
                <Td><NumInput value={cell.opening} onChange={(v) => setBucket(row.key, "opening", v)} testid={`fd-${row.key}-open`}/></Td>
                <Td><NumInput value={cell.received} onChange={(v) => setBucket(row.key, "received", v)} testid={`fd-${row.key}-recv`}/></Td>
                <Td><NumInput value={cell.expenditure} onChange={(v) => setBucket(row.key, "expenditure", v)} testid={`fd-${row.key}-exp`}/></Td>
                <Td className="font-semibold text-slate-900">{fmtMoney(closing)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3">
        <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">Does the sum of closing balances match the bank account?</Label>
        <div className="mt-1 flex gap-2">
          {[["Yes", true], ["No", false]].map(([lbl, val]) => (
            <button key={lbl} type="button" onClick={() => setBank(val)}
              className={`h-10 px-4 rounded-md border font-semibold ${monthly.fund_details.bankBalanceMatches === val ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              data-testid={`fd-bank-${lbl.toLowerCase()}`}>{lbl}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Cook Cum Helper Payment ──────────────────────────────────────────────
function CookHelperForm({ monthly, setMonthly }) {
  const rows = monthly.cook_cum_helper || [];
  const setRow = (i, field, v) => setMonthly((m) => {
    const next = [...(m.cook_cum_helper || [])];
    next[i] = { ...next[i], [field]: v };
    return { ...m, cook_cum_helper: next };
  });
  const addRow = () => setMonthly((m) => ({ ...m, cook_cum_helper: [...(m.cook_cum_helper || []), { name: "", gender: "", category: "", paymentMode: "", amountReceived: "" }] }));
  const delRow = (i) => setMonthly((m) => ({ ...m, cook_cum_helper: (m.cook_cum_helper || []).filter((_, idx) => idx !== i) }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm border">
        <thead className="bg-slate-50 border-b">
          <tr><Th>S.No.</Th><Th>Cook Name</Th><Th>Gender</Th><Th>Category</Th><Th>Payment Mode</Th><Th>Amount (₹)</Th><Th/></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b" data-testid={`cook-row-${i}`}>
              <Td>{i + 1}</Td>
              <Td><Input value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} className="h-10" data-testid={`cook-${i}-name`}/></Td>
              <Td>
                <select value={r.gender} onChange={(e) => setRow(i, "gender", e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid={`cook-${i}-gender`}>
                  <option value="">—</option><option value="M">M</option><option value="F">F</option>
                </select>
              </Td>
              <Td>
                <select value={r.category} onChange={(e) => setRow(i, "category", e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid={`cook-${i}-cat`}>
                  <option value="">—</option><option>SC</option><option>ST</option><option>OBC</option><option>GEN</option>
                </select>
              </Td>
              <Td>
                <select value={r.paymentMode} onChange={(e) => setRow(i, "paymentMode", e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid={`cook-${i}-mode`}>
                  <option value="">—</option><option>Cash</option><option>Bank</option>
                </select>
              </Td>
              <Td><NumInput value={r.amountReceived} onChange={(v) => setRow(i, "amountReceived", v)} testid={`cook-${i}-amt`}/></Td>
              <Td>{rows.length > 1 && <button type="button" onClick={() => delRow(i)} className="text-slate-400 hover:text-red-600 text-xs">Remove</button>}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button type="button" variant="outline" onClick={addRow} className="mt-3" data-testid="cook-add">+ Add Cook</Button>
    </div>
  );
}

// ─── Food Grains ─────────────────────────────────────────────────────────
function FoodGrainsForm({ monthly, setMonthly, auto }) {
  const setFG = (cat, food, field, v) => setMonthly((m) => ({
    ...m, food_grains: { ...m.food_grains,
      [cat]: { ...(m.food_grains[cat] || {}),
        [food]: { ...(m.food_grains[cat]?.[food] || {}), [field]: v } } }
  }));
  return (
    <div className="overflow-x-auto">
      <p className="text-xs text-slate-500 mb-2">Rice <b>Consumption</b> is auto-summed from Daily Entry. Wheat is manual (Daily Entry doesn't capture wheat).</p>
      <table className="w-full text-left text-sm border">
        <thead className="bg-slate-50 border-b">
          <tr><Th>Category</Th><Th>Food</Th><Th>Opening (kg)</Th><Th>Received (kg)</Th><Th>Consumption (kg)</Th><Th>Closing (kg)</Th></tr>
        </thead>
        <tbody>
          {CATS.flatMap((c) => ["wheat", "rice"].map((food) => {
            const cell = monthly.food_grains?.[c.key]?.[food] || {};
            const cons = food === "rice" ? num(auto.rice_kg[c.key]) : num(cell.consumption);
            const opening = num(cell.opening); const received = num(cell.received);
            const closing = opening + received - cons;
            return (
              <tr key={`${c.key}-${food}`} className="border-b" data-testid={`fg-${c.key}-${food}`}>
                <Td>{c.label}</Td>
                <Td className="font-semibold">{food === "wheat" ? "Wheat" : "Rice"}</Td>
                <Td><NumInput value={cell.opening} onChange={(v) => setFG(c.key, food, "opening", v)} testid={`fg-${c.key}-${food}-open`}/></Td>
                <Td><NumInput value={cell.received} onChange={(v) => setFG(c.key, food, "received", v)} testid={`fg-${c.key}-${food}-recv`}/></Td>
                <Td className={food === "rice" ? "text-slate-600" : ""}>
                  {food === "rice"
                    ? <>{formatRiceKg(cons)} <span className="text-[10px] text-emerald-700">AUTO</span></>
                    : <NumInput value={cell.consumption} onChange={(v) => setFG(c.key, food, "consumption", v)} testid={`fg-${c.key}-${food}-cons`}/>}
                </Td>
                <Td className="font-semibold text-slate-900">{formatRiceKg(closing)}</Td>
              </tr>
            );
          }))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Health Status ────────────────────────────────────────────────────────
function HealthForm({ monthly, setMonthly }) {
  const set = (field, v) => setMonthly((m) => ({ ...m, health_status: { ...m.health_status, [field]: v } }));
  const fields = [
    { key: "ifaBoys", label: "IFA Tablets — Boys (classes 1–8)" },
    { key: "ifaGirls", label: "IFA Tablets — Girls (classes 1–8)" },
    { key: "rbskScreened", label: "Children screened by RBSK team" },
    { key: "rbskReferred", label: "Children referred by RBSK team" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map((f) => (
        <div key={f.key}>
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{f.label}</Label>
          <Input type="number" min="0" value={monthly.health_status?.[f.key] ?? ""}
            onChange={(e) => set(f.key, e.target.value)} className="h-11 mt-1" data-testid={`health-${f.key}`}/>
        </div>
      ))}
    </div>
  );
}

// ─── Inspection ───────────────────────────────────────────────────────────
function InspectionForm({ monthly, setMonthly }) {
  const set = (field, v) => setMonthly((m) => ({ ...m, inspection: { ...m.inspection, [field]: v } }));
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">School Inspection Done?</Label>
        <div className="mt-1 flex gap-2">
          {["Yes", "No"].map((v) => (
            <button key={v} type="button" onClick={() => set("schoolInspectionDone", v)}
              className={`h-10 px-4 rounded-md border font-semibold ${monthly.inspection.schoolInspectionDone === v ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              data-testid={`insp-done-${v.toLowerCase()}`}>{v}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { key: "taskForce", label: "By Members of Task Force" },
          { key: "districtOfficials", label: "By District Officials" },
          { key: "blockOfficials", label: "By Block/Taluka Level Officials" },
          { key: "smcMembers", label: "By SMC Members" },
        ].map((f) => (
          <div key={f.key}>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">{f.label}</Label>
            <Input value={monthly.inspection?.[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder="Notes / dates / names" className="h-11 mt-1" data-testid={`insp-${f.key}`}/>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Th/Td helpers ────────────────────────────────────────────────────────
const Th = ({ children, className = "" }) => <th className={`p-2 text-xs uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`p-2 text-slate-700 border-r last:border-r-0 ${className}`}>{children}</td>;

// ─── Printable MDCF form ─────────────────────────────────────────────────
function MDCFPrintable({ report, monthly, school, year, month }) {
  const auto = report?.automatic || {};
  const monthLabel = `${MONTHS[month - 1]} ${year}`;
  const fd = monthly.fund_details || {};
  const cc = fd.cookingCost || {};
  const closingCC = (k) => num(cc[k]?.opening) + num(cc[k]?.received) - num(auto.cooking_cost?.[k]);
  const closingBucket = (k) => num(fd[k]?.opening) + num(fd[k]?.received) - num(fd[k]?.expenditure);
  const fg = monthly.food_grains || {};
  const foodCons = (cat, food) => food === "rice" ? num(auto.rice_kg?.[cat]) : num(fg[cat]?.wheat?.consumption);
  const foodClose = (cat, food) => num(fg[cat]?.[food]?.opening) + num(fg[cat]?.[food]?.received) - foodCons(cat, food);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 mdcf-print" data-testid="mdcf-preview">
      <div className="text-center border-b-2 border-slate-800 pb-3">
        <h2 className="font-display font-extrabold text-lg sm:text-xl text-slate-900">Pradhan Mantri Poshan Shakti Nirman (PM POSHAN)</h2>
        <p className="text-sm font-semibold text-slate-700 mt-1">School Monthly Data Capture Format (MDCF)</p>
      </div>

      {/* 1. School Details */}
      <FormSection num="1" title="School Details">
        <table className="w-full text-xs border">
          <tbody>
            <Row label="Month-Year" value={monthLabel} colSpan={3}/>
            <Row label="UDISE Code" value={school.udise}/>
            <Row label="School Name" value={school.school_name}/>
            <Row label="Type" value={monthly.school_details?.schoolType || ""}/>
            <Row label="Category" value={monthly.school_details?.schoolCategory || ""}/>
            <Row label="State / UT" value={monthly.school_details?.state || ""}/>
            <Row label="District" value={monthly.school_details?.district || ""}/>
            <Row label="Block/NP" value={monthly.school_details?.block || ""}/>
            <Row label="Village/Ward" value={monthly.school_details?.villageWard || ""}/>
            <Row label="Kitchen Type" value={monthly.school_details?.kitchenType || ""}/>
            <Row label="NGO/SHG" value={monthly.school_details?.ngoShg || ""}/>
            <Row label="Enrolment (total)" value={monthly.school_details?.enrolmentTotal || ""}/>
          </tbody>
        </table>
      </FormSection>

      {/* 2. Meals Availed Status */}
      <FormSection num="2" title="Meals Availed Status">
        <table className="w-full text-xs border">
          <thead className="bg-slate-50">
            <tr><Th>Row</Th><Th>Bal Vatika</Th><Th>Primary</Th><Th>Upper Primary</Th></tr>
          </thead>
          <tbody>
            <tr className="border-b"><Td>Enrolment during the month</Td>
              <Td>{monthly.school_details?.enrolment?.balvatika || "—"}</Td>
              <Td>{monthly.school_details?.enrolment?.primary || "—"}</Td>
              <Td>{monthly.school_details?.enrolment?.upper_primary || "—"}</Td></tr>
            <tr className="border-b"><Td>Number of School days during month</Td>
              <Td colSpan={3} className="text-center">{auto.school_days ?? "—"}</Td></tr>
            <tr className="border-b"><Td>Actual number of days MDM served</Td>
              <Td colSpan={3} className="text-center">{auto.mdm_days ?? "—"}</Td></tr>
            <tr><Td>Total Meals served during the month</Td>
              <Td>{auto.total_meals?.balvatika ?? 0}</Td>
              <Td>{auto.total_meals?.primary ?? 0}</Td>
              <Td>{auto.total_meals?.upper_primary ?? 0}</Td></tr>
          </tbody>
        </table>
        {auto.total_meals?.high_school > 0 && (
          <p className="mt-2 text-[11px] text-slate-500">Supplementary: High School (IX–X) meals recorded in Daily Entry — {auto.total_meals.high_school}. Not part of the official MDCF table above.</p>
        )}
      </FormSection>

      {/* 3. Fund Details */}
      <FormSection num="3" title="Fund Details (in ₹)">
        <table className="w-full text-xs border">
          <thead className="bg-slate-50">
            <tr><Th>Component</Th><Th>Opening</Th><Th>Received</Th><Th>Expenditure</Th><Th>Closing</Th></tr>
          </thead>
          <tbody>
            {CATS.map((c) => (
              <tr key={c.key} className="border-b">
                <Td>Cooking Cost — {c.label}</Td>
                <Td>{fmtMoney(cc[c.key]?.opening)}</Td>
                <Td>{fmtMoney(cc[c.key]?.received)}</Td>
                <Td>{fmtMoney(auto.cooking_cost?.[c.key] || 0)}</Td>
                <Td>{fmtMoney(closingCC(c.key))}</Td>
              </tr>
            ))}
            {[["cookCumHelper","Cook Cum Helper"],["mme","School Expenses : MME Expenses"]].map(([k, lbl]) => (
              <tr key={k} className="border-b">
                <Td>{lbl}</Td>
                <Td>{fmtMoney(fd[k]?.opening)}</Td>
                <Td>{fmtMoney(fd[k]?.received)}</Td>
                <Td>{fmtMoney(fd[k]?.expenditure)}</Td>
                <Td>{fmtMoney(closingBucket(k))}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs">Sum of closing balances matches bank account? <b>{fd.bankBalanceMatches === true ? "Yes" : fd.bankBalanceMatches === false ? "No" : "—"}</b></p>
      </FormSection>

      {/* 4. Cook Cum Helper */}
      <FormSection num="4" title="Cook Cum Helper Payment Details">
        <table className="w-full text-xs border">
          <thead className="bg-slate-50"><tr><Th>S.No.</Th><Th>Cook Name</Th><Th>Gender</Th><Th>Category</Th><Th>Payment Mode</Th><Th>Amount (₹)</Th></tr></thead>
          <tbody>
            {(monthly.cook_cum_helper || []).map((r, i) => (
              <tr key={i} className="border-b">
                <Td>{i + 1}</Td><Td>{r.name || "—"}</Td><Td>{r.gender || "—"}</Td>
                <Td>{r.category || "—"}</Td><Td>{r.paymentMode || "—"}</Td>
                <Td>{r.amountReceived ? fmtMoney(r.amountReceived) : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </FormSection>

      {/* 5. Food Grains */}
      <FormSection num="5" title="Food Grains Details (in KG.)">
        <table className="w-full text-xs border">
          <thead className="bg-slate-50"><tr><Th>Category</Th><Th>Food</Th><Th>Opening</Th><Th>Received</Th><Th>Consumption</Th><Th>Closing</Th></tr></thead>
          <tbody>
            {CATS.flatMap((c) => ["wheat","rice"].map((food) => (
              <tr key={`${c.key}-${food}`} className="border-b">
                <Td>{c.label}</Td>
                <Td>{food === "wheat" ? "Wheat" : "Rice"}</Td>
                <Td>{fg[c.key]?.[food]?.opening ? formatRiceKg(fg[c.key][food].opening) : "—"}</Td>
                <Td>{fg[c.key]?.[food]?.received ? formatRiceKg(fg[c.key][food].received) : "—"}</Td>
                <Td>{food === "rice" ? formatRiceKg(auto.rice_kg?.[c.key] || 0) : (fg[c.key]?.wheat?.consumption ? formatRiceKg(fg[c.key].wheat.consumption) : "—")}</Td>
                <Td>{formatRiceKg(foodClose(c.key, food))}</Td>
              </tr>
            )))}
          </tbody>
        </table>
      </FormSection>

      {/* 6. Health */}
      <FormSection num="6" title="Children Health Status">
        <table className="w-full text-xs border">
          <tbody>
            <Row label="IFA Tablets — Boys" value={monthly.health_status?.ifaBoys ?? "—"}/>
            <Row label="IFA Tablets — Girls" value={monthly.health_status?.ifaGirls ?? "—"}/>
            <Row label="RBSK Screened" value={monthly.health_status?.rbskScreened ?? "—"}/>
            <Row label="RBSK Referred" value={monthly.health_status?.rbskReferred ?? "—"}/>
          </tbody>
        </table>
      </FormSection>

      {/* 7. Inspection */}
      <FormSection num="7" title="School Inspection">
        <table className="w-full text-xs border">
          <tbody>
            <Row label="School Inspection Done" value={monthly.inspection?.schoolInspectionDone || "—"}/>
            <Row label="By Task Force" value={monthly.inspection?.taskForce || "—"}/>
            <Row label="By District Officials" value={monthly.inspection?.districtOfficials || "—"}/>
            <Row label="By Block/Taluka Officials" value={monthly.inspection?.blockOfficials || "—"}/>
            <Row label="By SMC Members" value={monthly.inspection?.smcMembers || "—"}/>
          </tbody>
        </table>
      </FormSection>

      <FormSection num="8" title="Untoward Incidents">
        <p className="text-sm">Number of Untoward Incidents Occurred: <b>{monthly.untoward_incidents ?? 0}</b></p>
      </FormSection>

      {/* Signatures */}
      <div className="mt-8 grid grid-cols-2 gap-6 text-xs">
        <div className="pt-10 border-t border-slate-400 text-center">Signature of the SMC Chairperson / Gram Pradhan</div>
        <div className="pt-10 border-t border-slate-400 text-center">Signature of Head Teacher</div>
      </div>
    </div>
  );
}

const FormSection = ({ num, title, children }) => (
  <div className="mt-4">
    <div className="font-display font-bold text-sm text-slate-900 mb-2 border-l-4 border-emerald-600 pl-2">{num}. {title}</div>
    {children}
  </div>
);
const Row = ({ label, value, colSpan }) => (
  <tr className="border-b"><Td className="font-semibold w-1/3">{label}</Td><Td colSpan={colSpan || 3}>{String(value ?? "").length ? value : "—"}</Td></tr>
);

// ─── CSV export ───────────────────────────────────────────────────────────
function exportCsv(report, monthly) {
  if (!report) return toast.error("Nothing to export");
  const rows = [];
  const auto = report.automatic || {};
  rows.push(["PM POSHAN Monthly MDCF"]);
  rows.push(["Month", `${MONTHS[report.month - 1]} ${report.year}`]);
  rows.push(["UDISE", report.school_profile?.udise, "School", report.school_profile?.school_name]);
  rows.push([]);
  rows.push(["Meals Availed Status"]);
  rows.push(["Category", "Enrolment", "Total Meals"]);
  ["balvatika","primary","upper_primary"].forEach((c) => rows.push([c, monthly.school_details?.enrolment?.[c] ?? "", auto.total_meals?.[c] ?? 0]));
  rows.push(["School Days", auto.school_days, "MDM Days", auto.mdm_days, "Holidays", auto.holidays]);
  rows.push([]);
  rows.push(["Fund Details (₹)"]);
  rows.push(["Component", "Opening", "Received", "Expenditure", "Closing"]);
  ["balvatika","primary","upper_primary"].forEach((c) => {
    const cell = monthly.fund_details?.cookingCost?.[c] || {};
    const exp = auto.cooking_cost?.[c] || 0;
    rows.push([`Cooking Cost ${c}`, num(cell.opening), num(cell.received), exp, num(cell.opening)+num(cell.received)-exp]);
  });
  rows.push(["Rice consumption (kg)"]);
  ["balvatika","primary","upper_primary"].forEach((c) => rows.push([c, formatRiceKg(auto.rice_kg?.[c] || 0)]));
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const safeSchool = (report.school_profile?.school_name || "school").replace(/[^\w]+/g, "_");
  a.download = `PM_POSHAN_MDCF_${safeSchool}_${MONTHS[report.month - 1]}_${report.year}.csv`;
  a.click(); URL.revokeObjectURL(a.href);
  toast.success("CSV downloaded");
}
